import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Mountain, Flag, Users, Building2, CreditCard, HelpCircle, Check, ShoppingBag, ArrowLeft } from 'lucide-react';
import { bookingAPI, addOnsAPI } from '../services/api';
import { formatStayOptionLabel } from '../utils/formatStayOptionLabel.js';
import { bookingRouteRef } from '../utils/bookingRouteRef';
import { onlineTotalWithGatewayFee } from '../utils/paymentGateway.js';
import { formatBookingPackageDateLabel } from '../utils/bookingPackageDates.js';
import LoadingIndicator from '../components/LoadingIndicator';
import './BookingAddOns.css';

const ADDON_CATEGORIES = {
  gear: 'GEAR RENTALS',
  activity: 'OPTIONAL ACTIVITIES',
  stay: 'ADDITIONAL STAY OPTIONS'
};

const ADDON_DEFAULT_UNIT = 'TRIP';

const formatInr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

export default function BookingAddOns() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [availableAddOns, setAvailableAddOns] = useState([]);
  const [addOnQuantities, setAddOnQuantities] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await bookingAPI.getById(id);
        if (cancelled) return;
        const data = res.data?.data;
        setBooking(data || null);
        const trekId = data?.batch?.trekId || data?.batch?.trek?.id;
        if (trekId) {
          const addOnRes = await addOnsAPI.getByTrekId(trekId);
          if (cancelled) return;
          const list = addOnRes.data?.data || [];
          setAvailableAddOns(list);
          setAddOnQuantities((prev) => {
            const next = { ...prev };
            list.forEach((a) => {
              if (next[a.id] === undefined) next[a.id] = 0;
            });
            return next;
          });
        }
      } catch {
        if (!cancelled) {
          setBooking(null);
          setAvailableAddOns([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const setAddOnQuantity = (addOnId, delta) => {
    setAddOnQuantities((prev) => {
      const next = { ...prev };
      const cur = next[addOnId] ?? 0;
      next[addOnId] = Math.max(0, cur + delta);
      return next;
    });
  };

  const selectedAddOns = useMemo(
    () =>
      availableAddOns
        .filter((a) => (addOnQuantities[a.id] || 0) > 0)
        .map((a) => ({
          id: a.id,
          name: a.name,
          price: Number(a.price),
          quantity: addOnQuantities[a.id]
        })),
    [availableAddOns, addOnQuantities]
  );

  const addOnTotal = selectedAddOns.reduce(
    (sum, o) => sum + (Number(o.price) || 0) * (o.quantity || 0),
    0
  );
  const subtotal = addOnTotal;
  const gstRate = 0.05;
  const gst = Math.round(subtotal * gstRate);
  const totalWithGst = subtotal + gst;
  const onlineAddonsPay = onlineTotalWithGatewayFee(totalWithGst);

  if (loading) {
    return (
      <div className="book-page">
        <div className="book-inner">
          <div className="book-loading">
            <LoadingIndicator label="Loading add-ons..." />
          </div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="book-page">
        <div className="book-inner">
          <p className="book-loading">Booking not found.</p>
          <Link to="/bookings">Back to My Bookings</Link>
        </div>
      </div>
    );
  }

  const trek = booking.batch?.trek;
  const batch = booking.batch;
  const stayOptionsList = Array.isArray(trek?.stayOptions) && trek.stayOptions.length > 0 ? trek.stayOptions : [];
  const stayOptionName = stayOptionsList.find((o) => String(o.id) === String(booking?.stayOption))?.name;
  const stayLabel = formatStayOptionLabel(stayOptionName, booking?.stayOption);
  const joinFromName =
    batch?.trek?.packages?.find((p) => p.id === booking?.joinFromPackageId)?.name || 'Dehradun';
  const travelerLabel =
    Number(booking?.travelerCount) === 1 ? '1 Traveller' : `${booking?.travelerCount} Travellers`;

  const handlePayOffice = () => {
    window.alert('Add-ons payment at office flow will be implemented here.');
  };

  const handlePayOnline = () => {
    window.alert('Online payment for add-ons will be implemented here.');
  };

  return (
    <div className="addons-page">
      <div className="addons-shell">
        <header className="addons-header">
          <Link to="/bookings" className="addons-back-link">
            <ArrowLeft size={16} />
            Back to My Bookings
          </Link>
          <button type="button" className="addons-help-btn">
            <HelpCircle size={18} />
            Need Help?
          </button>
        </header>

        <div className="addons-stepper">
          <div className="addons-step-item done">
            <div className="addons-step-circle"><Check size={18} /></div>
            <span>Details</span>
          </div>
          <div className="addons-step-line" />
          <div className="addons-step-item done">
            <div className="addons-step-circle"><Check size={18} /></div>
            <span>Payment</span>
          </div>
          <div className="addons-step-line" />
          <div className="addons-step-item active">
            <div className="addons-step-circle">3</div>
            <span>Add-ons</span>
          </div>
        </div>

        <div className="addons-layout">
          <section className="addons-main-card">
            <div className="addons-main-head">
              <h2>Enhance Your Adventure</h2>
              <p>Customize your experience with optional rentals, activities, and stay upgrades.</p>
            </div>

            {availableAddOns.length === 0 ? (
              <p className="addons-empty">No add-ons configured for this trip right now.</p>
            ) : (
              (() => {
                const byCategory = {};
                availableAddOns.forEach((a) => {
                  const cat = (a.type || 'activity').toLowerCase();
                  const key = ADDON_CATEGORIES[cat] ? cat : 'activity';
                  if (!byCategory[key]) byCategory[key] = [];
                  byCategory[key].push(a);
                });
                const order = ['gear', 'activity', 'stay'];
                return order
                  .filter((k) => byCategory[k]?.length)
                  .map((catKey) => (
                    <div key={catKey} className="addons-category">
                      <h3>{ADDON_CATEGORIES[catKey]}</h3>
                      <div className="addons-items">
                        {byCategory[catKey].map((addOn) => {
                          const qty = addOnQuantities[addOn.id] ?? 0;
                          const unit = addOn.priceUnit || addOn.unit || ADDON_DEFAULT_UNIT;
                          return (
                            <article key={addOn.id} className={`addons-item ${qty > 0 ? 'selected' : ''}`}>
                              <div className="addons-item-img">
                                <img
                                  src={addOn.imageUrl || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=120'}
                                  alt={addOn.name}
                                />
                              </div>
                              <div className="addons-item-body">
                                <h4>{addOn.name}</h4>
                                {addOn.description && <p>{addOn.description}</p>}
                                <strong>{formatInr(addOn.price)} <span>/ {unit}</span></strong>
                              </div>
                              <div className="addons-qty">
                                <button
                                  type="button"
                                  onClick={() => setAddOnQuantity(addOn.id, -1)}
                                  disabled={qty <= 0}
                                  aria-label="Decrease"
                                >
                                  −
                                </button>
                                <span>{qty}</span>
                                <button
                                  type="button"
                                  onClick={() => setAddOnQuantity(addOn.id, 1)}
                                  aria-label="Increase"
                                >
                                  +
                                </button>
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    </div>
                  ));
              })()
            )}
          </section>

          <aside className="addons-summary-wrap">
            <div className="addons-summary-card">
              <h3><ShoppingBag size={18} /> Booking Summary</h3>
              <table className="addons-summary-table">
                <tbody>
                  <tr>
                    <td>Selected Add-ons</td>
                    <td>{formatInr(addOnTotal)}</td>
                  </tr>
                </tbody>
              </table>
              {selectedAddOns.length > 0 && (
                <ul className="addons-selected-list">
                  {selectedAddOns.map((a) => (
                    <li key={a.id}>{a.quantity}x {a.name}</li>
                  ))}
                </ul>
              )}

              <table className="addons-summary-table">
                <tbody>
                  <tr>
                    <td>Subtotal</td>
                    <td>{formatInr(subtotal)}</td>
                  </tr>
                  <tr>
                    <td>GST (5%)</td>
                    <td>{formatInr(gst)}</td>
                  </tr>
                </tbody>
              </table>

              <div className="addons-total">
                <span>Total Payable</span>
                <strong>{formatInr(totalWithGst)}</strong>
              </div>

              <div className="addons-pay-actions">
                <button
                  type="button"
                  className="addons-pay-office"
                  onClick={handlePayOffice}
                  disabled={addOnTotal <= 0}
                >
                  <span><Building2 size={16} /> Pay At Office</span>
                  <span>{formatInr(totalWithGst)}</span>
                </button>
                <button
                  type="button"
                  className="addons-pay-online"
                  onClick={handlePayOnline}
                  disabled={addOnTotal <= 0}
                >
                  <span><CreditCard size={16} /> Pay Online</span>
                  <span className="addons-pay-online-right">
                    <strong>{formatInr(onlineAddonsPay.payableTotal)}</strong>
                    <small>+2% gateway ({formatInr(onlineAddonsPay.gatewayCharge)})</small>
                  </span>
                </button>
              </div>

              <p className="addons-disclaimer">
                By proceeding, you agree to the <a href="/terms">Booking Terms</a> and <a href="/safety">Safety Guidelines</a>.
              </p>
            </div>
          </aside>
        </div>

        <section className="addons-review-card">
          <div className="addons-review-head">
            <h3>Review Booking</h3>
            <Link to={`/bookings/confirmed/${encodeURIComponent(bookingRouteRef(booking))}`}>Edit Details</Link>
          </div>
          <div className="addons-review-grid">
            <div>
              <p>TREK SELECTION</p>
              <h4><Mountain size={16} /> {trek?.name}</h4>
              <span>
                Batch:{' '}
                {formatBookingPackageDateLabel(booking, (d) =>
                  new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                )}
              </span>
            </div>
            <div>
              <p>TRAVELLING PACKAGE</p>
              <h4>{joinFromName}</h4>
              <p style={{ marginTop: 12 }}>STAY TYPE</p>
              <h4>{stayLabel}</h4>
            </div>
            <div>
              <p>JOIN US FROM</p>
              <h4><Flag size={16} /> {joinFromName}</h4>
            </div>
            <div>
              <p>PARTICIPANTS</p>
              <h4><Users size={16} /> {travelerLabel}</h4>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}


